import {
  MacroPlaceholderContext,
  isHtmlMacroPhrase,
  macroPhraseToPlainText,
  resolveMacroPhrase,
} from '@chiroup/core/functions/macroPlaceholders';
import { createContext, use, useCallback } from 'react';

/**
 * The patient whose data fills macro placeholders. Patient pages provide it;
 * anywhere it is absent (team chat, settings) placeholders insert as tokens.
 */
export const MacroPatientContext = createContext<MacroPlaceholderContext | null>(null);

export type MacroActions = {
  /** Opens the add-macro modal from a `/` dropdown. `onClose` runs after the
   *  modal closes, whether or not a macro was saved, so the field that opened
   *  it can take focus back. */
  openAddMacro?: (options?: { onClose?: () => void }) => void;
};

/** App-level macro actions. Left empty where the add-macro modal is not mounted. */
export const MacroActionsContext = createContext<MacroActions>({});

export type MacroInsertFormat = 'text' | 'html';

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** A macro written before rich output, inserted into a rich-text field: its
 *  line breaks become hard breaks so they survive as more than whitespace. */
const plainPhraseToHtml = (phrase: string): string => escapeHtml(phrase).replace(/\n/g, '<br>');

const BLOCK_TAG = /<(p|ul|ol|li|blockquote|h[1-6]|hr|div|pre)\b/i;
const ANY_TAG = /<[a-z][^>]*>/i;

const decodeEntities = (value: string): string =>
  value
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&');

/**
 * A single-paragraph macro drops its `<p>` wrapper so it lands inline at the
 * cursor; block content inserted mid-paragraph would split the paragraph the
 * user is typing in. Multi-block output is left as blocks.
 */
const inlineIfSingleParagraph = (html: string): string => {
  const match = html.trim().match(/^<p(?:\s[^>]*)?>([\s\S]*)<\/p>$/i);

  return match && !BLOCK_TAG.test(match[1]) ? match[1] : html;
};

/**
 * Returns a function that prepares a macro's stored phrase for insertion: fills
 * placeholders from the surrounding patient context, then renders it for the
 * target — rich output as text (line breaks and list markers kept) for a
 * plain-text field, or plain output as HTML for a rich-text field.
 */
export const useMacroPhrase = () => {
  const placeholderContext = use(MacroPatientContext);

  return useCallback(
    (phrase: string, format: MacroInsertFormat): string => {
      const resolved = resolveMacroPhrase(phrase || '', placeholderContext);

      if (format === 'text') {
        return macroPhraseToPlainText(resolved);
      }

      const html = isHtmlMacroPhrase(resolved)
        ? inlineIfSingleParagraph(resolved)
        : plainPhraseToHtml(resolved);

      // Tiptap inserts content that parses to bare text as the original
      // string, entities and all, so tag-free output goes in as plain text.
      return ANY_TAG.test(html) ? html : decodeEntities(html);
    },
    [placeholderContext]
  );
};
